import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MenuItem } from '../../hooks/useSidebarMenu';
import {
    TbLayout2,
    TbLayoutGridFilled,
    TbChartAreaLine,
    TbChartAreaLineFilled,
    TbBuildings,
    TbBuildingCommunity,
    TbUsers,
    TbUserFilled,
    TbUser,
    TbChartBar,
    TbChartHistogram,
    TbSettings,
    TbSettingsFilled,
    TbBuildingCog,
    TbChevronRight,
    TbCreditCard,
    TbCreditCardFilled,
    TbList,
    TbReceipt,
    TbReceiptFilled,
    TbBriefcase,
    TbBriefcaseFilled,
    TbCalendarEvent,
    TbCalendarEventFilled,
    TbCalendar,
    TbCalendarFilled,
    TbAdjustmentsHorizontal,
} from 'react-icons/tb';
import { RiChatVoiceAiLine, RiChatVoiceAiFill, RiChatThreadLine, RiChatThreadFill } from "react-icons/ri";
import { BiStore, BiSolidStore, BiSolidBuildingHouse, BiBuildingHouse } from "react-icons/bi";
import { PiUsersThreeFill, PiUsersThree } from "react-icons/pi";
import { HiMiniUsers, HiOutlineUsers } from "react-icons/hi2";
interface SidebarItemProps {
    item: MenuItem;
    collapsed: boolean;
    isMobile: boolean;
    onLinkClick: () => void;
}

// Icon mapper
const iconMap: Record<string, React.ReactNode> = {
    'tb-layout2': <TbLayout2 />,
    'tb-layout2-filled': <TbLayoutGridFilled />,
    'tb-chart-line': <TbChartAreaLine />,
    'tb-chart-line-filled': <TbChartAreaLineFilled />,
    'tb-buildings': <BiBuildingHouse />,
    'tb-buildings-filled': <BiSolidBuildingHouse />,
    'pi-users': <HiOutlineUsers />,
    'pi-users-filled': <HiMiniUsers />,
    'rb-chatbot': <RiChatVoiceAiLine />,
    'rb-chatbot-filled': <RiChatVoiceAiFill />,
    'tb-chart-bar': <TbChartAreaLine />,
    'tb-chart-bar-filled': <TbChartAreaLineFilled />,
    'tb-cpu': <RiChatThreadLine />,
    'tb-cpu-filled': <RiChatThreadFill />,
    'tb-settings': <TbSettings />,
    'tb-settings-filled': <TbSettingsFilled />,
    'bi-building': <BiStore />,
    'bi-building-filled': <BiSolidStore />,
    'tb-building-cog': <TbBuildingCog />,
    'tb-users': <TbUsers />,
    'tb-users-filled': <PiUsersThreeFill />,
    'tb-credit-card': <TbCreditCard />,
    'tb-credit-card-filled': <TbCreditCardFilled />,
    'tb-list': <TbList />,
    'tb-receipt': <TbReceipt />,
    'tb-receipt-filled': <TbReceiptFilled />,
    'tb-user': <TbUser />,
    'tb-user-filled': <TbUserFilled />,
    'tb-briefcase': <TbBriefcase />,
    'tb-briefcase-filled': <TbBriefcaseFilled />,
    'tb-calendar-event': <TbCalendarEvent />,
    'tb-calendar-event-filled': <TbCalendarEventFilled />,
    'tb-calendar': <TbCalendar />,
    'tb-calendar-filled': <TbCalendarFilled />,
    'tb-adjustments-horizontal': <TbAdjustmentsHorizontal />,
};

const getIcon = (iconName: string): React.ReactNode => {
    return iconMap[iconName] || null;
};

const SidebarItem: React.FC<SidebarItemProps> = ({ item, collapsed, isMobile, onLinkClick }) => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

    // Auto-expand if child is active
    useEffect(() => {
        if (item.children) {
            const isChildActive = item.children.some(child =>
                location.pathname === child.path || location.pathname.startsWith(child.path + '/')
            );
            if (isChildActive) {
                setIsOpen(true);
            }
        }
    }, [location.pathname, item.children]);

    const toggleSubmenu = (e: React.MouseEvent) => {
        if (item.children) {
            e.preventDefault();
            if (collapsed && !isMobile) return; // Don't toggle in collapsed mode (hover handles it usually, or we disable it)
            setIsOpen(!isOpen);
        } else {
            onLinkClick();
        }
    };

    const showLabel = !collapsed || isMobile;

    if (item.children) {
        return (
            <div className={`nav-item-group ${isOpen ? 'open' : ''}`}>
                <div
                    className={`nav-link parent-link ${isActive ? 'active' : ''} ${isOpen ? 'expanded' : ''}`}
                    onClick={toggleSubmenu}
                >
                    <div className="nav-icon">
                        {isActive ? getIcon(item.icon.active as string) : getIcon(item.icon.default as string)}
                    </div>

                    {showLabel && (
                        <>
                            <span className="nav-text">{item.title}</span>
                            {item.badge && <span className="nav-badge">{item.badge}</span>}
                            <span className={`chevron-icon ${isOpen ? 'rotate' : ''}`}>
                                {isOpen ? <TbChevronRight /> : <TbChevronRight />}
                            </span>
                        </>
                    )}
                </div>

                {/* Submenu */}
                <div className={`submenu-wrapper ${isOpen ? 'expanded' : ''}`}>
                    <div className="submenu-content">
                        {item.children.map(child => (
                            <NavLink
                                key={child.path}
                                to={child.path}
                                onClick={onLinkClick}
                                className={({ isActive }) => `nav-link sub-link ${isActive ? 'active' : ''}`}
                            >
                                {({ isActive: navIsActive }) => (
                                    <>
                                        <div className="nav-icon">
                                            {navIsActive ? getIcon(child.icon.active as string) : getIcon(child.icon.default as string)}
                                        </div>
                                        <span className="nav-text">{child.title}</span>
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <NavLink
            to={item.path}
            onClick={onLinkClick}
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
        >
            {({ isActive }) => (
                <>
                    <div className="nav-icon">
                        {isActive ? getIcon(item.icon.active as string) : getIcon(item.icon.default as string)}
                    </div>
                    {showLabel && (
                        <>
                            <span className="nav-text">{item.title}</span>
                            {item.badge && <span className="nav-badge">{item.badge}</span>}
                        </>
                    )}
                </>
            )}
        </NavLink>
    );
};

export default SidebarItem;
